import { ChangeDetectionStrategy, Component, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { UserDto } from '@user/services/user/models/user.dto';
import { ProfileDialogComponent } from '../../../features/profile/components/profile-dialog/profile-dialog.component';
import { OutsideClick } from '../../directives/outside-click/outside-click.directive';
import { AuthService } from '../../services/auth/auth.service';
import { MeService } from '../../services/me/me.service';
import { ThemeEnum } from '../../services/theme/models/enums/theme.enum';
import { ThemeService } from '../../services/theme/theme.service';
import { ButtonComponent } from '../button/button.component';
import { DialogService } from '../dialog';
import { IconComponent } from '../icon/icon.component';
import { MenuService } from '../menu/services/menu.service';
import { TextComponent } from '../text/text.component';

@Component({
  selector: 'l-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [IconComponent, ButtonComponent, OutsideClick, TextComponent]
})
export class HeaderComponent {
  private readonly dialogService = inject(DialogService);

  constructor(
    private readonly meService: MeService,
    private readonly menuService: MenuService,
    private readonly authService: AuthService,
    private readonly themeService: ThemeService
  ) {}

  public open: WritableSignal<boolean> = signal(false);

  public mobileOpen: Signal<boolean> = this.menuService.mobileOpen;

  public theme: Signal<ThemeEnum> = this.themeService.theme;

  public me: Signal<UserDto | null> = this.meService.me;

  protected readonly userInitial = computed(() => {
    const name = this.me()?.name?.trim() ?? '';
    return (name[0] ?? '?').toUpperCase();
  });

  protected readonly userInitials = computed(() => {
    const name = this.me()?.name?.trim() ?? '';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return (parts[0]?.[0] ?? '?').toUpperCase();
  });

  protected readonly themeEnum = ThemeEnum;

  public toggleTheme(): void {
    this.themeService.theme = this.theme() === ThemeEnum.DARK ? ThemeEnum.LIGHT : ThemeEnum.DARK;
  }

  public toggleMobileMenu(): void {
    this.menuService.toggle();
  }

  public toggleUserMenu(): void {
    this.open.update(value => !value);
  }

  public closeUserMenu(): void {
    this.open.set(false);
  }

  public async logout(): Promise<void> {
    this.closeUserMenu();
    await this.authService.logout();
  }

  public openProfile(): void {
    this.open.set(false);
    this.dialogService.create({
      content: ProfileDialogComponent,
      position: 'top-right',
      animation: 'from-right'
    });
  }
}
